import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useGetIcmStatementDetail } from "@/icm/query";
import {
  getPayBreakdownStatementPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";
import { TechnicianStatementExperience } from "./technician-statement-experience";
import { useTechnicianPayrollBase } from "./use-technician-payroll-base";
import { isStatementOwnedByTech } from "./utils";

export const TechnicianStatementPage = () => {
  const navigate = useNavigate();
  const { statementId, periodStart } = useParams<{
    statementId?: string;
    periodStart?: string;
  }>();
  const {
    businessId,
    techUser,
    isUserLoading,
    periodOptions,
    onPeriodChange,
    isPayPeriodsLoading,
  } = useTechnicianPayrollBase({
    redirectToDefaultPeriod: false,
  });

  const { data: statement, isLoading: isStatementLoading } =
    useGetIcmStatementDetail(businessId, statementId);

  const isOwnStatement = isStatementOwnedByTech(
    statement,
    techUser?.crm_external_id,
  );

  useEffect(() => {
    if (!statement || !statementId || periodStart === statement.period_start) {
      return;
    }

    navigate(
      getPayBreakdownStatementPath(statement.period_start, statementId),
      {
        replace: true,
      },
    );
  }, [navigate, periodStart, statement, statementId]);

  if (!isUserLoading && !techUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isStatementLoading && statement && !isOwnStatement) {
    return <Navigate to={PAY_BREAKDOWN_PATH} replace />;
  }

  return (
    <TechnicianStatementExperience
      statement={isOwnStatement ? (statement ?? null) : null}
      isLoading={isUserLoading || isPayPeriodsLoading || isStatementLoading}
      periodOptions={periodOptions}
      selectedPeriodStart={statement?.period_start ?? periodStart}
      onPeriodChange={onPeriodChange}
      emptyTitle="We couldn't load that statement"
      emptyDescription="Try another pay period or return to your payroll home."
    />
  );
};
